import {
    LastPeriodicCheck,
    Machine,
    PeriodicCheck,
    PeriodicCheckDraftResponse,
    PeriodicCheckPayload,
    Project,
} from '~/types';

export type PeriodicCheckSessionMode = 'editable' | 'editable_draft' | 'submitted_pending';

const ACCEPTANCE_INSPECTION = 2;

export const machineChecksField = (
    inspectionType?: number,
): 'checks' | 'acceptanceInspectionChecks' =>
    Number(inspectionType) === ACCEPTANCE_INSPECTION ? 'acceptanceInspectionChecks' : 'checks';

export const usableCheckId = (id?: string | null): string | null => {
    if (id == null || id === '') {
        return null;
    }
    const value = String(id);
    if (value.startsWith('local-')) {
        return null;
    }
    return value;
};

/** Queue rows store split image fields; the checklist UI reads `images` / `im`. */
export const savedCheckToUiPayload = (check: PeriodicCheckPayload): PeriodicCheckPayload => {
    const hasSplit = check.existingImages != null || check.newImages != null;
    if (!hasSplit) {
        return check;
    }
    const images = [
        ...(check.existingImages ?? []),
        ...((check.newImages ?? []) as unknown as (string | File)[]),
    ];
    const im = [...(check.existingIm ?? []), ...(check.newIm ?? [])];
    const { existingImages, existingIm, newImages, newIm, ...rest } = check;
    return { ...rest, images, im };
};

export const mergeCheckPayload = (
    template: PeriodicCheck | PeriodicCheckPayload,
    draft?: PeriodicCheckPayload,
): PeriodicCheckPayload => {
    const base = template as PeriodicCheckPayload;
    return {
        ...template,
        status: draft?.status ?? base.status ?? null,
        issues: draft?.issues ?? base.issues ?? [],
        quantity: draft?.quantity ?? base.quantity ?? 0,
        images: draft?.images ?? base.images,
        im: draft?.im ?? base.im,
    };
};

export const mergeSavedChecksIntoTemplates = (
    templates: Array<PeriodicCheck | PeriodicCheckPayload>,
    savedLines?: PeriodicCheckPayload[],
): PeriodicCheckPayload[] => {
    const byId = new Map(
        (savedLines ?? []).map(check => [String(check.id), savedCheckToUiPayload(check)]),
    );
    return templates.map(template => mergeCheckPayload(template, byId.get(String(template.id))));
};

export const applyPeriodicCheckToMachineJson = (
    machineJson: Machine,
    equipmentId: string | number,
    inspectionType: number,
    lastCheck: LastPeriodicCheck,
    savedChecks: PeriodicCheckPayload[],
    projectId?: Project['id'],
): { machine: Machine; changed: boolean } => {
    const targetId = String(equipmentId);
    const checksField = machineChecksField(inspectionType);
    const metaField = Number(inspectionType) === ACCEPTANCE_INSPECTION
        ? 'lastAcceptanceInspection'
        : 'lastDailyCheck';

    const patch = (slice: Machine): Machine => {
        const templates = (slice[checksField] || []) as PeriodicCheck[];
        const merged = templates.length
            ? mergeSavedChecksIntoTemplates(templates, savedChecks)
            : savedChecks.map(savedCheckToUiPayload);
        const checkData: PeriodicCheckDraftResponse = {
            id: lastCheck.id,
            checks: merged,
            inspectionType,
            machineId: slice.id,
            projectId,
        };
        return {
            ...slice,
            [metaField]: lastCheck,
            [checksField]: merged,
            checkId: usableCheckId(lastCheck.id) ?? slice.checkId,
            checkData,
        };
    };

    if (String(machineJson.id) === targetId) {
        return { machine: patch(machineJson), changed: true };
    }

    let changed = false;
    const linkedMachines = machineJson.linkedMachines?.map(linked => {
        if (String(linked.id) !== targetId) {
            return linked;
        }
        changed = true;
        return patch(linked);
    });
    if (!changed) {
        return { machine: machineJson, changed: false };
    }
    return { machine: { ...machineJson, linkedMachines }, changed: true };
};
